import { useMap } from 'react-leaflet';
import { AppBar, Box, Fab, Icon, Toolbar } from '@mui/material';

import MapIcon from '@mui/icons-material/Map';
import SearchIcon from '@mui/icons-material/Search';
import NearMeIcon from '@mui/icons-material/NearMe';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';

import { combineClassNamesConditional } from '../../../Lib/Utils/combineClassNames';
import { IActionForOwner } from '../../../Lib/Actions/actions';
import { useOnlineStatus } from '../../../Lib/Network/useOnlineStatus';
import { ToolbarFlowOrientation } from '../../../Settings/settingsTypes';
import { useSettings } from '../../../Settings/useSettings';
import { ITileLayerInfo } from '../../MapComponents/layers';
import { OnlineStatus } from '../../../Lib/Network/types';
import { IPlaceSearchResult } from '../../Search/placeSearchTypes';
import { IMapLocationTracker } from '../../LocationInfo/types';
import MainMenu from './MainMenu';

import toolbarStyles from "../Toolbar.module.css";


interface MapMainToolBarProps {
    currentTitleLayer?: ITileLayerInfo | null;
    settingsAction: IActionForOwner<{ map: L.Map }, void>;
    downloadAction: IActionForOwner<{ map: L.Map, urlTemplate: string, urlSubdomains: string[] }, void>;
    searchAction: IActionForOwner<{ map: L.Map, biasBounds?: L.LatLngBounds | null }, IPlaceSearchResult>;
    changeLayerAction: IActionForOwner<void, ITileLayerInfo>;
    locationTracker: IMapLocationTracker
}

const MapMainToolBar = (props: MapMainToolBarProps): JSX.Element => {

    const map =  useMap();
    const settings = useSettings();
    const onlineStatus = useOnlineStatus();

    const canDownload = props?.currentTitleLayer?.canDownload ?? false;

    return (
        <AppBar 
            position="fixed" 
            color="primary" 
            sx={{ top: 'auto', bottom: 0, height: '60px', direction: settings.ui.toolbarFlowOrientation === ToolbarFlowOrientation.RightHanded ? 'ltr' : 'rtl'}}
            className={toolbarStyles.app_bar_root}
        >
            <Toolbar>

                {onlineStatus === OnlineStatus.Online && <Icon title="You are online"><WifiIcon /></Icon>}
                {onlineStatus === OnlineStatus.Offline && <Icon title="You are offline"><WifiOffIcon /></Icon>}

                <Box className={toolbarStyles.centre_box}>
                    <Fab 
                        color="secondary" 
                        onClick={() => props.searchAction!.start({ map: map, biasBounds: map.getBounds() })} 
                        className={toolbarStyles.centre_box_button}
                    >
                        <SearchIcon />
                    </Fab>

                    <Fab 
                        color="secondary" 
                        onClick={() => props.changeLayerAction!.start()} 
                        className={toolbarStyles.centre_box_button}
                    >
                        <MapIcon />
                    </Fab>

                {props.locationTracker.isEnabled && 
                    <Fab 
                        color="default" 
                        className={combineClassNamesConditional([[toolbarStyles.centre_box_button, true], [toolbarStyles.centre_box_button_active, props.locationTracker.isTrackingActive]])} 
                        onClick={() => props.locationTracker.toggleTracking()}
                    >
                        <NearMeIcon />
                    </Fab>
                }
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <MainMenu 
                    onSettings={() => props.settingsAction!.start({ map: map })}
                    onDownload={() => props.downloadAction!.start({ map: map, urlTemplate: props.currentTitleLayer!.urlTemplate, urlSubdomains: props.currentTitleLayer!.urlSubdomains ?? [] })}
                    canDownload={canDownload}
                />

            </Toolbar>
        </AppBar>
    );

};

export default MapMainToolBar;